import { Box, Container, Typography, Paper, Divider, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { COLORS } from '@elevair/shared';

// Helper component for sections
const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Box sx={{ mb: 4 }}>
    <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 2, color: COLORS.secondary }}>
      {title}
    </Typography>
    {children}
  </Box>
);

export default function TermsPage() {
  return (
    <>
      <Helmet>
        <title>Terms of Service - Elevair</title>
        <meta name="description" content="Elevair Terms of Service - Read our terms and conditions for using the Elevair platform." />
      </Helmet>

      <Box sx={{ minHeight: '100vh', bgcolor: COLORS.background, py: 6 }}>
        <Container maxWidth="md">
          {/* Header */}
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Link component={RouterLink} to="/" sx={{ textDecoration: 'none' }}>
              <Typography
                variant="h4"
                sx={{
                  color: COLORS.primary,
                  fontWeight: 700,
                  fontFamily: 'Georgia, serif',
                }}
              >
                Elevair
              </Typography>
            </Link>
          </Box>

          <Paper sx={{ p: { xs: 3, md: 5 }, borderRadius: 2 }}>
            <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 1, color: COLORS.secondary }}>
              Terms of Service
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              Last updated: February 8, 2026
            </Typography>

            <Divider sx={{ mb: 4 }} />

            {/* Section 1 */}
            <Section title="1. Acceptance of Terms">
              <Typography paragraph>
                By accessing or using Elevair ("Service"), you agree to be bound by these Terms of Service ("Terms").
                If you do not agree to these Terms, you may not access or use the Service.
              </Typography>
              <Typography paragraph>
                Elevair is operated by Elevair Inc. ("we," "us," or "our"). These Terms apply to all users of the Service,
                including users who are service professionals, their clients, and visitors.
              </Typography>
            </Section>

            {/* Section 2 */}
            <Section title="2. Description of Service">
              <Typography paragraph>
                Elevair is a business management platform designed for service professionals. Our Service includes:
              </Typography>
              <Box component="ul" sx={{ pl: 3, '& li': { mb: 0.5 } }}>
                <li><Typography>Client relationship management (CRM)</Typography></li>
                <li><Typography>Service and booking management</Typography></li>
                <li><Typography>Invoicing and payment tracking</Typography></li>
                <li><Typography>Task and project management</Typography></li>
                <li><Typography>Notes and reminders</Typography></li>
                <li><Typography>Public landing pages for lead generation</Typography></li>
                <li><Typography>AI-powered strategy plan generation</Typography></li>
                <li><Typography>Community features for professional networking</Typography></li>
              </Box>
            </Section>

            {/* Section 3 */}
            <Section title="3. Account Registration">
              <Typography paragraph>
                To use certain features of the Service, you must register for an account. When you register, you agree to:
              </Typography>
              <Box component="ul" sx={{ pl: 3, '& li': { mb: 0.5 } }}>
                <li><Typography>Provide accurate, current, and complete information</Typography></li>
                <li><Typography>Maintain and promptly update your account information</Typography></li>
                <li><Typography>Maintain the security of your password and account</Typography></li>
                <li><Typography>Accept responsibility for all activities that occur under your account</Typography></li>
                <li><Typography>Notify us immediately of any unauthorized use of your account</Typography></li>
              </Box>
              <Typography paragraph sx={{ mt: 2 }}>
                You must be at least 18 years old to create an account and use the Service.
              </Typography>
            </Section>

            {/* Section 4 */}
            <Section title="4. Subscription Plans and Billing">
              <Typography paragraph>
                Elevair offers various subscription tiers (Free, Trial, Launch, Rise, Summit) with different features and limits.
                By selecting a paid subscription, you agree to pay the applicable fees.
              </Typography>
              <Typography paragraph>
                <strong>No Long-Term Contracts:</strong> All Elevair subscriptions are offered on a month-to-month or
                annual basis at your choice. There are no long-term contracts, commitments, or cancellation penalties.
                You are never locked in.
              </Typography>
              <Typography paragraph>
                <strong>Free Trial:</strong> New accounts may be eligible for a 14-day free trial.
                No credit card is required for the trial period. By activating a free trial, you agree to all
                provisions of these Terms, including the Prohibited Uses (Section 7) and Intellectual Property
                (Section 8) provisions. The free trial is provided solely for your evaluation of the Service for
                your own legitimate business use. Use of the free trial for competitive analysis, reverse engineering,
                or any purpose related to developing a competing product is expressly prohibited and constitutes
                a material breach of these Terms.
              </Typography>
              <Typography paragraph>
                <strong>Billing:</strong> Paid subscriptions are billed in advance on a monthly or annual basis.
                Your subscription will automatically renew at the end of each billing period unless you cancel.
              </Typography>
              <Typography paragraph>
                <strong>No Refunds:</strong> All subscription fees are non-refundable. When you pay for a subscription period,
                you have access to the Service for that entire period. We do not provide partial refunds for unused time
                or early cancellations.
              </Typography>
              <Typography paragraph>
                <strong>Cancellation:</strong> You may cancel your subscription at any time through your account settings.
                When you cancel, you will continue to have access to your paid features until the end of your current
                billing period. After that, your account will revert to the Free tier. To stop using Elevair entirely,
                simply do not renew—there are no cancellation fees or penalties.
              </Typography>
              <Typography paragraph>
                <strong>Price Changes:</strong> We reserve the right to change subscription prices. Any price changes
                will take effect at the start of your next billing period. We will provide notice of price changes
                at least 30 days in advance.
              </Typography>
            </Section>

            {/* Section 5 */}
            <Section title="5. User Content and Data">
              <Typography paragraph>
                You retain ownership of all content and data you submit to the Service ("User Content").
                By submitting User Content, you grant us a limited license to use, store, and process that content
                solely to provide the Service to you.
              </Typography>
              <Typography paragraph>
                You are responsible for:
              </Typography>
              <Box component="ul" sx={{ pl: 3, '& li': { mb: 0.5 } }}>
                <li><Typography>All User Content you submit</Typography></li>
                <li><Typography>Ensuring you have the right to submit such content</Typography></li>
                <li><Typography>Maintaining your own backups of important data</Typography></li>
                <li><Typography>Complying with all applicable laws regarding your User Content</Typography></li>
              </Box>
            </Section>

            {/* Section 6 */}
            <Section title="6. Community Guidelines">
              <Typography paragraph>
                If you use our Community Hub feature, you agree to:
              </Typography>
              <Box component="ul" sx={{ pl: 3, '& li': { mb: 0.5 } }}>
                <li><Typography>Treat other members with respect</Typography></li>
                <li><Typography>Not post content that is offensive, harmful, or inappropriate</Typography></li>
                <li><Typography>Not spam, harass, or bully other users</Typography></li>
                <li><Typography>Not share misleading or false information</Typography></li>
                <li><Typography>Not solicit other users' clients or engage in predatory practices</Typography></li>
                <li><Typography>Not share confidential business information about your clients</Typography></li>
              </Box>
              <Typography paragraph sx={{ mt: 2 }}>
                We reserve the right to remove any content and suspend or terminate accounts that violate these guidelines.
              </Typography>
            </Section>

            {/* Section 7 */}
            <Section title="7. Prohibited Uses">
              <Typography paragraph>
                You may not use the Service to:
              </Typography>
              <Box component="ul" sx={{ pl: 3, '& li': { mb: 0.5 } }}>
                <li><Typography>Violate any applicable laws or regulations</Typography></li>
                <li><Typography>Infringe on the intellectual property rights of others</Typography></li>
                <li><Typography>Transmit malware, viruses, or other harmful code</Typography></li>
                <li><Typography>Attempt to gain unauthorized access to our systems</Typography></li>
                <li><Typography>Interfere with or disrupt the Service</Typography></li>
                <li><Typography>Use the Service for any fraudulent or illegal purpose</Typography></li>
                <li><Typography>Resell or redistribute the Service without authorization</Typography></li>
                <li><Typography>Reverse engineer, decompile, disassemble, or otherwise attempt to derive the source code, underlying algorithms, data models, or architecture of the Service or any part thereof</Typography></li>
                <li><Typography>Copy, clone, replicate, or create derivative works of the Service, its features, user interface, design, or functionality, in whole or in part, for any purpose including but not limited to building a competing product or service</Typography></li>
                <li><Typography>Use the Service, including any free trial or free tier, for the purpose of competitive analysis, benchmarking, or developing a product or service that competes with Elevair</Typography></li>
                <li><Typography>Scrape, crawl, or use any automated means to extract data, content, designs, workflows, or other information from the Service for use in building or improving another product or service</Typography></li>
                <li><Typography>Access the Service, including the free trial, under false pretenses, using fictitious information, or on behalf of a competitor for the purpose of evaluating, copying, or replicating the Service</Typography></li>
              </Box>
            </Section>

            {/* Section 8 */}
            <Section title="8. Intellectual Property">
              <Typography paragraph>
                The Service and its original content, features, functionality, design, user interface, workflows,
                architecture, source code, and underlying technology are owned by Elevair Inc. and are protected
                by international copyright, trademark, patent, trade secret, and other intellectual property laws.
              </Typography>
              <Typography paragraph>
                You may not copy, modify, distribute, sell, or lease any part of the Service without our
                prior written consent. This includes, without limitation, the visual design, layout, look and feel,
                feature set, user experience flows, and business logic of the Service.
              </Typography>
              <Typography paragraph>
                All rights not expressly granted to you in these Terms are reserved by Elevair Inc. Your access to
                the Service, including any free trial period, does not grant you any license or right to use, reproduce,
                or create derivative works based on the Service's proprietary technology or intellectual property.
              </Typography>
            </Section>

            {/* Section 9 */}
            <Section title="9. Third-Party Services">
              <Typography paragraph>
                The Service may integrate with or contain links to third-party services (such as payment processors,
                email services, or calendar applications). We are not responsible for the content, privacy policies,
                or practices of third-party services.
              </Typography>
            </Section>

            {/* Section 10 */}
            <Section title="10. Disclaimer of Warranties">
              <Typography paragraph sx={{ textTransform: 'uppercase', fontSize: '0.9rem' }}>
                The Service is provided "as is" and "as available" without warranties of any kind,
                either express or implied, including but not limited to implied warranties of merchantability,
                fitness for a particular purpose, and non-infringement.
              </Typography>
              <Typography paragraph>
                We do not warrant that the Service will be uninterrupted, secure, or error-free,
                or that any defects will be corrected.
              </Typography>
            </Section>

            {/* Section 11 */}
            <Section title="11. Limitation of Liability">
              <Typography paragraph sx={{ textTransform: 'uppercase', fontSize: '0.9rem' }}>
                To the maximum extent permitted by law, Elevair Inc. shall not be liable for any indirect,
                incidental, special, consequential, or punitive damages, or any loss of profits or revenues,
                whether incurred directly or indirectly, or any loss of data, use, goodwill, or other
                intangible losses.
              </Typography>
              <Typography paragraph>
                Our total liability for any claims arising from or related to the Service shall not exceed
                the amount you paid us in the twelve (12) months preceding the claim.
              </Typography>
            </Section>

            {/* Section 12 */}
            <Section title="12. Indemnification">
              <Typography paragraph>
                You agree to indemnify, defend, and hold harmless Elevair Inc. and its officers, directors,
                employees, and agents from any claims, liabilities, damages, losses, and expenses
                (including reasonable attorneys' fees) arising out of or related to your use of the Service
                or violation of these Terms.
              </Typography>
            </Section>

            {/* Section 13 */}
            <Section title="13. Termination">
              <Typography paragraph>
                We may terminate or suspend your account and access to the Service immediately,
                without prior notice, for any reason, including if you breach these Terms.
              </Typography>
              <Typography paragraph>
                Upon termination, your right to use the Service will immediately cease.
                You may request a copy of your data within 30 days of termination.
              </Typography>
            </Section>

            {/* Section 14 */}
            <Section title="14. Changes to Terms">
              <Typography paragraph>
                We reserve the right to modify these Terms at any time. We will provide notice of significant
                changes by posting the new Terms on this page and updating the "Last updated" date.
              </Typography>
              <Typography paragraph>
                Your continued use of the Service after changes become effective constitutes acceptance of
                the revised Terms.
              </Typography>
            </Section>

            {/* Section 15 */}
            <Section title="15. Remedies">
              <Typography paragraph>
                You acknowledge that any breach of Sections 7 or 8 of these Terms would cause irreparable harm
                to Elevair Inc. for which monetary damages would be inadequate. Accordingly, Elevair Inc. shall be
                entitled to seek injunctive or other equitable relief in addition to any other remedies available
                at law or in equity, without the necessity of proving actual damages or posting a bond.
              </Typography>
              <Typography paragraph>
                In the event of a breach of these Terms involving reverse engineering, cloning, or competitive misuse
                of the Service, you agree that Elevair Inc. may immediately terminate your account without notice and
                may pursue all available legal remedies, including claims for damages, disgorgement of profits, and
                recovery of attorneys' fees and costs.
              </Typography>
            </Section>

            {/* Section 16 */}
            <Section title="16. Governing Law">
              <Typography paragraph>
                These Terms shall be governed by and construed in accordance with the laws of the State of Tennessee,
                United States, without regard to its conflict of law provisions.
              </Typography>
            </Section>

            {/* Section 17 */}
            <Section title="17. Contact Us">
              <Typography paragraph>
                If you have any questions about these Terms, please contact us at:
              </Typography>
              <Typography paragraph>
                <strong>Email:</strong> support@elevair.app
                <br />
                <strong>Website:</strong> https://elevair.app
              </Typography>
            </Section>

            <Divider sx={{ my: 4 }} />

            {/* Footer Links */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3 }}>
              <Link component={RouterLink} to="/privacy" sx={{ color: COLORS.accent }}>
                Privacy Policy
              </Link>
              <Link component={RouterLink} to="/" sx={{ color: COLORS.accent }}>
                Back to Home
              </Link>
            </Box>
          </Paper>
        </Container>
      </Box>
    </>
  );
}
